import { Button } from "@/components/ui/button";
import { useUser } from "@/features/auth/hooks/use-user";
import {
  HEYUP_PHONE_E164,
  useCallHeyUpTeam,
} from "@/features/onboarding/heyup-team";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { CheckCircleIcon, PhoneIncomingIcon } from "@phosphor-icons/react";
import { formatPhoneNumberIntl } from "react-phone-number-input";

const display = (phone: string) => formatPhoneNumberIntl(phone) || phone;

/**
 * "Prenez rendez-vous avec l'équipe": the HeyUp number, and a button that has
 * the team call the client back on their own mobile.
 *
 * The button only shows for a number the account has proven (`phone_verified`
 * is `true` once the wizard's SMS code came back): the callback rings whatever
 * number it is given, and this one must be the client's. Without one, the
 * number to dial is all there is.
 */
export function HeyUpTeamContact({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation("onboarding");
  const { data: user } = useUser();
  const callMe = useCallHeyUpTeam();

  const mobile = user?.phone_verified === true ? user.phone : null;

  return (
    <div className={cn("space-y-2", compact ? "text-xs" : "text-sm")}>
      <p className={cn("text-muted-foreground", !compact && "text-center")}>
        {t(compact ? "team.hintShort" : "team.hint")}{" "}
        <a
          href={`tel:${HEYUP_PHONE_E164}`}
          className="text-foreground font-medium whitespace-nowrap hover:underline"
        >
          {display(HEYUP_PHONE_E164)}
        </a>
      </p>

      {mobile &&
        (callMe.isSuccess ? (
          <p
            className={cn(
              "flex items-center gap-1.5 font-medium",
              !compact && "justify-center",
            )}
          >
            <CheckCircleIcon
              className="text-primary h-4 w-4 shrink-0"
              weight="fill"
            />
            {t("team.calling", { number: display(mobile) })}
          </p>
        ) : (
          <>
            <Button
              type="button"
              size={compact ? "sm" : "default"}
              variant={compact ? "outline" : "default"}
              className="w-full"
              disabled={callMe.isPending}
              onClick={() => callMe.mutate(mobile)}
            >
              <PhoneIncomingIcon className="h-4 w-4" weight="fill" />
              {t(compact ? "team.callMeShort" : "team.callMe")}
            </Button>
            {callMe.isError && (
              <p className="text-destructive">
                {t("team.failed", { number: display(HEYUP_PHONE_E164) })}
              </p>
            )}
          </>
        ))}
    </div>
  );
}
